/**
 * Этап 4 / KS-07 — display a value that MAY be rich text.
 *
 * Some surfaces show text whose producer was migrated to HTML and text whose
 * producer was not (a recruiter's typed note, an older stored artifact, a prompt
 * left out of the migration on purpose). Rendering both correctly needs a decision
 * per value, and this is where it is made:
 *   · HTML → {@link RichTextView}, sanitized and styled as prose;
 *   · anything else → a plain `<p>` with `whitespace-pre-wrap`, byte-identical to
 *     how these surfaces rendered before.
 *
 * The plain branch deliberately does NOT route through the Markdown renderer. These
 * are places where a recruiter's line breaks and spacing are the meaning (a pasted
 * note, a manual context), and reflowing them into Markdown paragraphs would change
 * text nobody asked to change.
 */
import type { JSX } from 'react';
import { looksLikeHtml } from '@eer/shared';
import { RichTextView } from './RichTextView.js';

export interface RichTextOrPlainProps {
  /** RAW stored value. Sanitized downstream when it turns out to be HTML. */
  readonly value: unknown;
  readonly className?: string;
  readonly testId?: string;
}

/** Render a value as rich text when it is HTML, else as pre-wrapped plain text. */
export function RichTextOrPlain({
  value,
  className,
  testId,
}: RichTextOrPlainProps): JSX.Element | null {
  const text = String(value ?? '');
  if (looksLikeHtml(text)) {
    return <RichTextView value={text} className={className} testId={testId} />;
  }
  return (
    <p className={`whitespace-pre-wrap ${className ?? ''}`} data-testid={testId}>
      {text}
    </p>
  );
}
